import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box, Typography } from '@material-ui/core'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { IntegrationDots, IntegrationDotsProps } from '../integrationDots'
import { useFormStyles } from '../../lib/hooks/useFormStyles'
import { HelpTooltip } from '../helpTooltip'
import { DifferenceTooltip } from '../differenceTooltip'
import { descriptionHtmlForSave, normalizeDescriptionHtml } from '../../lib/htmlDescriptionUtility'
import { useRichTextEditorStyles } from './formRichTextEditor.styles'

interface FormRichTextEditorProps extends IntegrationDotsProps {
  name: string

  label: string

  required?: boolean

  disabled?: boolean

  marketplaceValue?: string

  description?: string
}

const TOOLBAR_MODULES = {
  toolbar: [
    [{header: [1, 2, 3, false]}],
    ['bold', 'italic', 'underline'],
    [{list: 'ordered'}, {list: 'bullet'}],
    ['link'],
    ['clean']
  ]
}

const EDITOR_FORMATS = [
  'header',
  'bold', 'italic', 'underline',
  'list',
  'link'
]

export const FormRichTextEditor = ({
  name,
  label,
  required,
  disabled,
  types,
  marketplaceValue,
  description
}: FormRichTextEditorProps) => {
  const classes = useFormStyles()
  const editorClasses = useRichTextEditorStyles()
  const {control} = useFormContext()

  const marketplaceHtml = useMemo(
    () => marketplaceValue ? normalizeDescriptionHtml(marketplaceValue) : undefined,
    [marketplaceValue]
  )

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      rules={{required: required ? 'Это поле обязательно' : false}}
      render={({field, fieldState}) => (
        <Box>
          <Box display="flex" alignItems="center" className={classes.root}>
            <Typography variant="subtitle1" className={classes.label}>
              {label}
            </Typography>
            <Box className={classes.dotsContainer}>
              <IntegrationDots types={types}/>
              {description && <HelpTooltip title={description}/>}
              {marketplaceHtml && <DifferenceTooltip title={marketplaceHtml} isHtml/>}
            </Box>
          </Box>
          <Box className={editorClasses.editorWrapper}>
            <ReactQuill
              theme="snow"
              value={field.value ?? ''}
              onChange={value => field.onChange(descriptionHtmlForSave(value))}
              readOnly={disabled}
              modules={TOOLBAR_MODULES}
              formats={EDITOR_FORMATS}
              className={fieldState.error ? editorClasses.editorError : undefined}
            />
          </Box>
          {fieldState.error && (
            <Typography color="error" variant="caption">
              {fieldState.error.message}
            </Typography>
          )}
        </Box>
      )}
    />
  )
}
